import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const CHATBASE_SCRIPT_SRC = 'https://www.chatbase.co/embed.min.js';
const CHATBASE_DOMAIN = 'www.chatbase.co';

/** Public marketing / support surfaces only — not the authenticated admin portal. */
const PUBLIC_CHATBASE_PATHS = new Set([
  '/',
  '/login',
  '/contact-us',
  '/support',
  '/company-terms',
  '/metrics',
  '/boost',
  '/features-overview',
  '/filters',
  '/keywords-descriptions',
  '/our-story',
  '/picking-plan',
  '/site-setup',
  '/tips-tricks',
  '/tuning-search',
]);

declare global {
  interface Window {
    chatbase?: ((...args: unknown[]) => unknown) & {
      q?: unknown[];
      open?: () => void;
      close?: () => void;
    };
  }
}

function getChatbotId(): string {
  return (process.env.REACT_APP_CHATBASE_CHATBOT_ID || '').trim();
}

function shouldShowChatbase(pathname: string): boolean {
  return PUBLIC_CHATBASE_PATHS.has(pathname);
}

function removeChatbaseDom(chatbotId: string): void {
  const script = document.getElementById(chatbotId);
  script?.remove();

  document
    .querySelectorAll(
      'iframe[src*="chatbase"], [class*="chatbase"], [id*="chatbase"]',
    )
    .forEach((el) => {
      if (el.id === chatbotId) {
        return;
      }
      el.remove();
    });
}

function ensureChatbaseStub(): void {
  let alreadyInitialized = false;
  try {
    alreadyInitialized =
      typeof window.chatbase === 'function' &&
      window.chatbase('getState') === 'initialized';
  } catch {
    alreadyInitialized = false;
  }
  if (alreadyInitialized) {
    return;
  }

  type QueueFn = ((...args: unknown[]) => unknown) & { q?: unknown[] };
  const queueFn: QueueFn = (...args: unknown[]) => {
    if (!queueFn.q) {
      queueFn.q = [];
    }
    queueFn.q.push(args);
  };
  queueFn.q = [];

  window.chatbase = new Proxy(queueFn, {
    get(target, prop) {
      if (prop === 'q') {
        return target.q;
      }
      return (...args: unknown[]) => target(prop, ...args);
    },
  }) as Window['chatbase'];
}

function loadChatbaseScript(chatbotId: string): void {
  if (document.getElementById(chatbotId)) {
    return;
  }

  ensureChatbaseStub();

  const onLoad = () => {
    if (document.getElementById(chatbotId)) {
      return;
    }
    const script = document.createElement('script');
    script.src = CHATBASE_SCRIPT_SRC;
    script.id = chatbotId;
    // Chatbase embed reads this custom property / attribute
    (script as HTMLScriptElement & { domain: string }).domain = CHATBASE_DOMAIN;
    script.setAttribute('domain', CHATBASE_DOMAIN);
    document.body.appendChild(script);
  };

  if (document.readyState === 'complete') {
    onLoad();
  } else {
    window.addEventListener('load', onLoad, { once: true });
  }
}

function tryOpenChatbase(): void {
  const open = window.chatbase?.open;
  if (typeof open === 'function') {
    open.call(window.chatbase);
    return;
  }
  if (typeof window.chatbase === 'function') {
    try {
      window.chatbase('open');
    } catch {
      // Widget may still be initializing
    }
  }
}

/**
 * Env-gated Chatbase embed for public pages.
 * Set REACT_APP_CHATBASE_CHATBOT_ID to enable; unset/empty disables.
 * Use /support?chat=1 to auto-open the bubble when ready.
 */
const ChatbaseWidget = (): null => {
  const { pathname, search } = useLocation();
  const chatbotId = getChatbotId();
  const enabled = Boolean(chatbotId) && shouldShowChatbase(pathname);

  useEffect(() => {
    if (!chatbotId) {
      return;
    }

    if (!enabled) {
      try {
        window.chatbase?.close?.();
      } catch {
        // ignore
      }
      removeChatbaseDom(chatbotId);
      return;
    }

    loadChatbaseScript(chatbotId);

    const params = new URLSearchParams(search);
    if (params.get('chat') !== '1') {
      return;
    }

    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      tryOpenChatbase();
      if (attempts >= 20) {
        window.clearInterval(timer);
      }
    }, 500);

    return () => window.clearInterval(timer);
  }, [chatbotId, enabled, search]);

  return null;
};

export default ChatbaseWidget;
